import { Caption, StyledReactSelect } from '@fountain/fountain-ui-components';
import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { WorkflowStageDetail } from 'api-clients/monolith';
import { produce } from 'immer';
import React, { FC, useContext } from 'react';
import { useIntl } from 'react-intl';

import {
  getActionErrorMessage,
  stageTypeWithReasons,
} from 'containers/WorkflowEditor/components/Rules/Action/constants';
import { RulesEditDataContext } from 'containers/WorkflowEditor/contexts/rulesEditDataContext';

import {
  PartnerRuleProps,
  SharedPartnerActionProps,
} from '../../PartnerRules/types';
import {
  RulesProps,
  SharedActionProps,
  SharedElseActionProps,
} from '../../types';
import messages from './messages';
import { ReasonSelect } from './ReasonSelect';
import { StageOptionWithMessage } from './StageOptionWithMessage';

const useStyles = makeStyles(theme => ({
  stageWrapper: {
    position: 'relative',
  },
  errorBelow: {
    left: 0,
    marginTop: theme.spacing(0.5),
    position: 'absolute',
    right: 0,
    top: '100%',
  },
}));

const FINAL_STAGE_TYPES = [
  'RejectedStage',
  'ArchivedStage',
  'HiredStage',
] as const;

type TargetStageType = {
  id: string;
  title: string;
  type: string;
  // eslint-disable-next-line camelcase
  stage_id: number;
};

export type MoveToStageProps = (
  | SharedActionProps
  | SharedPartnerActionProps
  | SharedElseActionProps
) & { disableFinalStagesForAiCondition?: boolean };

export const MoveToStage: FC<MoveToStageProps> = React.memo(
  ({
    action,
    ruleId,
    setRules,
    setPartnerRules,
    setStage,
    errors,
    disableFinalStagesForAiCondition = false,
  }) => {
    const intl = useIntl();
    const classes = useStyles();

    const {
      funnel_stages: funnelStages,
      is_ai_interview_stage: isAiInterviewStage,
    } = useContext(RulesEditDataContext);

    const shouldDisableFinalStages: boolean =
      (isAiInterviewStage ?? false) ||
      (disableFinalStagesForAiCondition ?? false);

    const selectedStage =
      funnelStages?.find(stage => stage.stage_id === action.ref_stage_1_id) ??
      null;

    const isFinalStageDisabled = (option: TargetStageType) =>
      Boolean(
        shouldDisableFinalStages &&
        FINAL_STAGE_TYPES.includes(
          option.type as (typeof FINAL_STAGE_TYPES)[number],
        ),
      );

    const onChangeStage = (stage: TargetStageType) => {
      if (isFinalStageDisabled(stage)) return;
      // PartnerStage handling
      if (setPartnerRules && ruleId) {
        setPartnerRules(
          produce((draftRules: PartnerRuleProps) => {
            const draftAction = draftRules[ruleId]?.action_attributes;

            draftAction.ref_stage_1_id = stage.stage_id;
          }),
        );
      }

      if (setRules && ruleId) {
        setRules(
          produce((draftRules: RulesProps) => {
            const draftActions =
              draftRules[ruleId]?.action_set_attributes?.actions_attributes ??
              [];

            const idx = draftActions.findIndex(act => act.id === action.id);

            draftActions[idx].ref_stage_1_id = stage.stage_id;
          }),
        );
      } else if (setStage) {
        setStage(
          produce((draftStage: WorkflowStageDetail) => {
            if (
              draftStage.type !== 'RuleStage' &&
              draftStage.type !== 'CustomStage' &&
              draftStage.type !== 'AssessmentStage' &&
              draftStage.type !== 'ComplianceStage'
            ) {
              return;
            }
            const elseAction =
              draftStage.additional_info.else_action_set_attributes
                ?.actions_attributes?.[0];

            if (elseAction) {
              elseAction.ref_stage_1_id = stage.stage_id;
            }
          }),
        );
      }
    };

    const selectedStageType = selectedStage?.type;
    const showReasonsDropdown =
      selectedStageType && stageTypeWithReasons[selectedStageType];

    const isSelectedStageFinal =
      selectedStage != null && isFinalStageDisabled(selectedStage);

    const aiTerminalStageError =
      shouldDisableFinalStages && isSelectedStageFinal
        ? intl.formatMessage(messages.aiTerminalStageValidationError)
        : undefined;

    const missingStageError = !action.ref_stage_1_id
      ? getActionErrorMessage('ref_stage_1', errors)
      : undefined;

    const selectError = aiTerminalStageError ? true : missingStageError;

    const aiNoFinalStageMessage = intl.formatMessage(
      messages.aiNoFinalStageMessage,
    );
    const stageOptions =
      funnelStages?.map((stage: TargetStageType) => ({
        ...stage,
        disabledMessage:
          shouldDisableFinalStages && isFinalStageDisabled(stage)
            ? aiNoFinalStageMessage
            : undefined,
      })) ?? [];

    const stageSelectProps = {
      value: selectedStage,
      options: stageOptions,
      getOptionLabel: (option: TargetStageType) => option.title,
      getOptionValue: (option: TargetStageType) => option.stage_id,
      onChange: onChangeStage,
      isOptionDisabled: isFinalStageDisabled,
      label: intl.formatMessage(messages.stage),
      'aria-label': intl.formatMessage(messages.stage),
      error: selectError,
      placeholder: '',
      required: true,
      components: shouldDisableFinalStages
        ? { Option: StageOptionWithMessage }
        : undefined,
    };

    return (
      <>
        <Grid item xs={6}>
          <div className={classes.stageWrapper}>
            <StyledReactSelect
              {...(stageSelectProps as React.ComponentProps<
                typeof StyledReactSelect
              >)}
            />
            {aiTerminalStageError && (
              <div className={classes.errorBelow}>
                <Caption color="error">{aiTerminalStageError}</Caption>
              </div>
            )}
          </div>
        </Grid>
        {showReasonsDropdown && (
          <Grid item xs={6}>
            <ReasonSelect
              action={action}
              ruleId={ruleId}
              setRules={setRules}
              setPartnerRules={setPartnerRules}
              setStage={setStage}
            />
          </Grid>
        )}
      </>
    );
  },
);
